export enum QuestionType {
  SINGLE_SELECTION = 'single_selection',
  SINGLE_RADIO_SELECTION = 'single_radio_selection',
  MULTI_SELECTION = 'multi_selection',
  FREE_TEXT = 'free_text',
  CONSENT = 'consent',
  IMAGE_UPLOAD = 'image_upload'
}

export interface EmptyQuestion<T extends QuestionType> {
  _id: string;
  position: number;
  section: number;
  question: string;
  answers: AnswerOption[];
  description_html?: string | null;
  image_url?: string | null;
  parent_question_id?: string | null;
  answer_id?: string | null;
  required: boolean;
  type: T;
  input?: StructuredInput | null;
  answer_selection?: AnswerSelection;
}

/**
 * Whether the question draws its answers for the patient to pick from. `hidden` means the
 * screen shows its content and a continue button instead, and collects no answer at all —
 * an information screen. Anything a patient must actively acknowledge keeps its selection
 * on screen, so that the tap is recorded.
 *
 * Absence is `displayed`: questionnaires authored before this existed, and clients that
 * predate it, both go on drawing the answers.
 */
export type AnswerSelection = 'hidden' | 'displayed';

export type StructuredInputType = 'text' | 'number';

export interface StructuredInputField {
  id: string;
  input_type: StructuredInputType;
  suffix?: string;
  placeholder?: string | number;
  decimals?: number;
  min?: number;
  max?: number;
}

export interface StructuredInputToggle {
  id: string;
  label: string;
  answer_template?: string;
  fields: StructuredInputField[];
}

/**
 * Supplementary rendering instructions for a free text question: the fields to collect,
 * optionally behind a choice of unit. Clients that predate it ignore it and keep showing
 * a plain text box, which is why it stays optional.
 */
export interface StructuredInput {
  toggles?: StructuredInputToggle[];
  fields?: StructuredInputField[];
  default_toggle?: string;
}

export interface AnswerOption {
  _id: string;
  value: string;
}

export interface AnyEmptyQuestion extends EmptyQuestion<QuestionType> {}

export interface EditableQuestion extends AnyEmptyQuestion {
  image?: File;
}

export interface Section {
  title?: string;
  section: number;
  questions: AnyEmptyQuestion[];
}

export interface Questionnaire {
  _id?: number | string;
  title: string;
  date_created?: Date;
  blocking_answers?: string[];
  sections: Section[];
}
